"use client";

import Image from "next/image";
import { motion } from "framer-motion";

type Paragraph = string;

export default function OurStory({
  eyebrow,
  title,
  paragraphs,
  imageSrc,
  imageAlt,
  imageCaption,
}: {
  eyebrow: string;
  title: string;
  paragraphs: Paragraph[];
  imageSrc: string;
  imageAlt: string;
  imageCaption?: string;
}) {
  return (
    <section className="border-y border-dashed border-white/15 bg-[#0b0b0d] py-24 md:py-32">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-2 md:gap-20 md:items-center">
          {/* Texte */}
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <p className="text-[10px] tracking-[0.3em] uppercase text-white/40 font-body">
              {eyebrow}
            </p>

            <h2 className="mt-6 font-heading text-4xl md:text-5xl leading-[1.1] font-light text-white">
              {title}
            </h2>

            <div className="mt-8 space-y-5">
              {paragraphs.map((p, i) => (
                <p key={i} className="text-base leading-relaxed text-white/65 font-body font-light">
                  {p}
                </p>
              ))}
            </div>
          </motion.div>

          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: 24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            className="relative"
          >
            <div className="relative aspect-[4/5] w-full overflow-hidden rounded-sm">
              <Image
                src={imageSrc}
                alt={imageAlt}
                fill
                className="object-cover"
                sizes="(max-width: 768px) 100vw, 50vw"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent" />
            </div>

            {imageCaption && (
              <p className="mt-4 text-[10px] tracking-[0.28em] uppercase text-white/35 font-body">
                {imageCaption}
              </p>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
